import * as React from 'react';
import Box from '@mui/material/Box';
import {
  FormControl,
  Button,
  Divider,
  Stack,
  Typography,
  Popover,
  DialogContentText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useStoreActions } from "easy-peasy";
import api from "../../../services/api";
import {
  Checkbox,
  TextField,
  Autocomplete,
  IntegerField
} from '../../../components';
import { nivelHierarquico } from './static';
import FloatField from '../../../components/FloatField';
import Ferramentas from './Ferramentas';
import { pt } from 'yup-locale-pt';
import Ecossistema from './Ecossistema';
import Eventos from './Eventos';
import Premiacoes from './Premiacoes';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import yupnanable from '../../../services/yup-nanable.js';

yup.setLocale(pt);

const DadosGerais = ({ ano, empresa }) => {
  let startLoading = useStoreActions(actions => actions.nav.startLoading);
  let stopLoading = useStoreActions(actions => actions.nav.stopLoading);

  const schema = yup
    .object({
      fomentoTalento: yup
        .boolean()
        .label(
          'Existem processos e/ou instrumentos na minha empresa para identificação de talentos intraempreededores(ex.: assessment de identificação de talentos; matriz pipeline talentos x soft skills; avaliação de softskills).'
        ),
      intraempreendedor: yup
        .boolean()
        .label(
          'Minha empresa incentiva e se relaciona com o intraempreendedorismo (promove capacitação no tema; conexão com a liga mundial intraempreendedora e empresas referências).'
        ),
      intraempreLista: yup
        .string()
        .nullable()
        .max(1000, 'Máximo 1000 caracteres')
        .label('Liste as inciativas de intraempreendedorismo (uma por linha)'),
      garantiaIntraemrpre: yup
        .boolean()
        .label(
          'Existe um processo estabelecido na minha empresa para garantir o desenvolvimento do intraemprendedorismo  (aquisição e retenção de talentos, edital de oficinas empreendedores e premiações de incentivo, medição do avanço do grau de intraemprendedorismo hackathon interno, etc).'
        ),
      inovacao: yup
        .boolean()
        .label(
          'Minha empresa fomenta outras iniciativas internas relacionadas à inovação, como programa de melhoria contínua.'
        ),
      inovacaoLista: yup
        .string()
        .nullable()
        .max(1000, 'Máximo 1000 caracteres')
        .label('Liste as inciativas de inovação (uma por linha)'),
      departInovacao: yup
        .boolean()
        .nullable()
        .label(
          'Existe uma área(s)/departamento(s) em minha empresa dedicados a Inovação'
        )
        .default(false),
      nivelHirarquia: yup
        .number()
        .nanable()
        .integer()
        .nullable()
        .label('Nível de hierarquia'),
      investimentoInovacao: yup
        .number()
        .nullable()
        .positive()
        .float()
        .nanable()
        .label('Inovação (R$)'),
      investimentoPdtech: yup
        .number()
        .nullable()
        .positive()
        .float()
        .nanable()
        .label('P&D Tech (R$)'),
      pePocs: yup
        .boolean()
        .nullable()
        .label(
          "Minha empresa realizou POC's com startups no ano de referência."
        )
        .default(false),
      peNumPocs: yup
        .number()
        .nanable()
        .integer()
        .positive()
        .nullable()
        .label('Número de trabalhos'),
      peHubs: yup
        .boolean()
        .nullable()
        .default(false)
        .label('Minha empresa participa de hubs de inovação'),
      peListaHubs: yup
        .string()
        .nullable()
        .max(1000, 'Máximo 1000 caracteres')
        .label(
          'Liste os hubs de inovação dos quais sua empresa participa (um email por linha)'
        ),
      peHackathons: yup
        .boolean()
        .nullable()
        .default(false)
        .label(
          'Minha empresa realizou hackathons/bootcamps externos no ano de referência'
        ),
      peNumHackathons: yup
        .number()
        .integer()
        .nanable()
        .positive()
        .nullable()
        .label('Número de Hackathons/bootcamps'),
      ferramentasInova: yup
        .boolean()
        .nullable()
        .label('Minha empresa não usa ferramentas para gerenciar a inovação.')
        .default(false),
      ecoInteracStartups: yup
        .boolean()
        .nullable()
        .label('Não houve interação com startups.')
        .default(false),
      observacoes: yup
        .string()
        .nullable()
        .max(1000, 'Máximo 1000 caracteres')
        .label('Observações Finais')
    })
    .required();

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema)
  });

  const {
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, isSubmitting }
  } = useFormRef;

  const loadDadosGerais = async mount => {
    startLoading();
    try {
      let getDados = await api.http.get(
        '09/dados-gerais/empresa/' + empresa + '/' + ano
      );
      if (mount.isMounted) {
        reset(getDados.data);
      }
      stopLoading();
    } catch (error) {
      if (error.response && error.response.status == 404) {
        if (mount.isMounted) {
          reset({
            ano: ano,
            fomentoTalento: false,
            intraempreendedor: false,
            intraempreLista: null,
            garantiaIntraemrpre: false,
            inovacao: false,
            inovacaoLista: null,
            departInovacao: false,
            nivelHirarquia: null,
            investimentoInovacao: '',
            investimentoPdtech: '',
            pePocs: false,
            peNumPocs: '',
            peHubs: false,
            peNumHubs: null,
            peHackathons: false,
            peNumHackathons: '',
            ferramentasInova: false,
            ecoInteracStartups: false,
            observacoes: ''
          });
          stopLoading();
        } else {
          stopLoading({
            message: error.toString(),
            severity: 'error'
          });
        }
      }
    }
  };

  const watchIntraempreendedor = watch('intraempreendedor');
  const watchInovacaoLista = watch('inovacao');
  const watchDepartInovacao = watch('departInovacao');
  const watchPePocs = watch('pePocs');
  const wachPeHubs = watch('peHubs');
  const watchPeHackathons = watch('peHackathons');

  const disableIntraempreendedor = () => {
    return !watchIntraempreendedor;
  };
  const disableInovacaoLista = () => {
    return !watchInovacaoLista;
  };
  const disableDepartInovacao = () => {
    return !watchDepartInovacao;
  };
  const disabledPePocs = () => {
    return !watchPePocs;
  };
  const disabledPeHubs = () => {
    return !wachPeHubs;
  };
  const disabledpeHackathons = () => {
    return !watchPeHackathons;
  };

  const submit = async data => {
    startLoading();
    try {
      let result;
      if (data.id) {
        result = await api.http.put(
          '09/dados-gerais/empresa/' + empresa + '/' + data.id,
          { ...data }
        );
      } else {
        result = await api.http.post('09/dados-gerais/empresa/' + empresa, {
          ...data,
          ano: ano
        });
      }
      reset(result.data);
      stopLoading({ severity: 'success' });
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error'
      });
      throw error;
    }
  };

  React.useEffect(() => {
    let mount = {
      isMounted: true
    };
    loadDadosGerais(mount);
    return () => {
      mount.isMounted = false;
    };
  }, [ano, empresa]);

  // POPOVER

  const [anchorElFomenDev, setAnchorElFomenDev] = React.useState(null);
  const [anchorElInova, setAnchorElInova] = React.useState(null);
  const [anchorElPeD, setAnchorElPeD] = React.useState(null);
  const [anchorElFerr, setAnchorElFerr] = React.useState(null);
  const [anchorElPE, setAnchorElPE] = React.useState(null);
  const [anchorElEco, setAnchorElEco] = React.useState(null);

  // HANDLE CLICKS
  const handleClickFormenDev = event => {
    setAnchorElFomenDev(event.currentTarget);
  };
  const handleClickInova = event => {
    setAnchorElInova(event.currentTarget);
  };
  const handleClickPeD = event => {
    setAnchorElPeD(event.currentTarget);
  };
  const handleClickFerr = event => {
    setAnchorElFerr(event.currentTarget);
  };
  const handleClickPE = event => {
    setAnchorElPE(event.currentTarget);
  };
  const handleClickEco = event => {
    setAnchorElEco(event.currentTarget);
  };

  // HANDLE CLOSES
  const handleCloseFomenDev = () => {
    setAnchorElFomenDev(null);
  };
  const handleCloseInova = () => {
    setAnchorElInova(null);
  };
  const handleClosePeD = () => {
    setAnchorElPeD(null);
  };
  const handleCloseFerr = () => {
    setAnchorElFerr(null);
  };
  const handleClosePE = () => {
    setAnchorElPE(null);
  };
  const handleCloseEco = () => {
    setAnchorElEco(null);
  };

  return (
    (<div>
      <Divider>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <h3>Fomento ao desenvolvimento de intraempreendedores</h3>
          <HelpOutlineIcon
            onClick={handleClickFormenDev}
            sx={{
              m: 2
            }}
          />
          <Popover
            open={Boolean(anchorElFomenDev)}
            anchorEl={anchorElFomenDev}
            onClose={handleCloseFomenDev}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <Typography sx={{ p: 2 }}>
              O objetivo desta seção é avaliar como as empresas identificam os
              talentos intraempreendedores, <br />
              se as elas são conectadas com iniciativas e/ou recebem suporte
              externo, e como elas são implementadas. <br />
              (RH/Novos Negócios/Tecnologia e Inovação) <br />
              <i>Definição</i>: <b>Intraempreendedorismo</b> é a habilidade de
              empreender dentro de uma organização. <br />
              Ou seja, é quando os colaboradores utilizam seu perfil
              empreendedor para trazer ideias inovadoras para <br />a empresa
              onde trabalham.
            </Typography>
          </Popover>
        </Stack>
      </Divider>
      <FormControl sx={{ textAlign: 'left', mb: 2 }}>
        <Checkbox
          field="fomentoTalento"
          schema={schema}
          useFormRef={useFormRef}
        />
      </FormControl>
      <FormControl sx={{ textAlign: 'left', mb: 2 }}>
        <Checkbox
          field="intraempreendedor"
          schema={schema}
          useFormRef={useFormRef}
        />
      </FormControl>
      <FormControl sx={{ textAlign: 'left', mb: 2 }} fullWidth>
        <TextField
          field="intraempreLista"
          multiline
          rows={4}
          schema={schema}
          useFormRef={useFormRef}
          disabled={Boolean(disableIntraempreendedor())}
          inputProps={{ maxLength: 1000 }}
        />
      </FormControl>
      <FormControl sx={{ textAlign: 'left', mb: 2 }}>
        <Checkbox
          field="garantiaIntraemrpre"
          schema={schema}
          useFormRef={useFormRef}
        />
      </FormControl>
      <FormControl sx={{ textAlign: 'left', mb: 2 }}>
        <Checkbox field="inovacao" schema={schema} useFormRef={useFormRef} />
      </FormControl>
      <FormControl sx={{ textAlign: 'left', mb: 2 }} fullWidth>
        <TextField
          field="inovacaoLista"
          multiline
          rows={4}
          schema={schema}
          useFormRef={useFormRef}
          disabled={Boolean(disableInovacaoLista())}
          inputProps={{ maxLength: 1000 }}
        />
      </FormControl>
      <Divider sx={{mt:3}}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <h3>Investimento em Inovação</h3>
          <HelpOutlineIcon
            onClick={handleClickInova}
            sx={{
              m: 2
            }}
          />
          <Popover
            open={Boolean(anchorElInova)}
            anchorEl={anchorElInova}
            onClose={handleCloseInova}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <Typography sx={{ p: 2 }}>
              O objetivo desta seção é coletar informações para meta de
              Investimento em Inovação.
              <br></br>
              (O material correspoindente está no: capítulo 4) Saiba mais
              clicando{' '}
              <a
                href="https://venidera.sharepoint.com/:b:/s/IBRAM/EcwgpqbMq_FAkSnixCwSvKkBDkm6YCANstEi2UdTc4fN2w?e=yAqIZr"
                target="_blank" rel="noreferrer"
              >
                aqui
              </a>
              .
            </Typography>
          </Popover>
        </Stack>
      </Divider>
      <FormControl sx={{ mb: 2, textAlign: 'left' }} fullWidth>
        <Checkbox
          field="departInovacao"
          schema={schema}
          useFormRef={useFormRef}
        />
      </FormControl>
      <Grid container spacing={2} sx={{
        alignItems: 'center',
        '& .MuiTypography-root': { textAlign: 'right' },
        '& .MuiGrid-root': { p: 1 },
        '& .MuiSvgIcon-root': { verticalAlign: 'middle' }
      }}>
        <Grid
          size={{
            md: 9
          }}>
          <Typography>
            Qual o nível hierárquico desta(s) área(s) na estrutura
            organizacional da empresa?
          </Typography>
        </Grid>
        <Grid
          size={{
            md: 3
          }}>
          <FormControl fullWidth>
            <Autocomplete
              field="nivelHirarquia"
              schema={schema}
              useFormRef={useFormRef}
              options={nivelHierarquico}
              disabled={Boolean(disableDepartInovacao())}
            />
          </FormControl>
        </Grid>

        <Grid
          size={{
            md: 9
          }}>
          <Typography>
            Durante o ano de referência, quanto a sua empresa investiu em
            inovação?
          </Typography>
        </Grid>
        <Grid
          size={{
            md: 3
          }}>
          <FormControl fullWidth>
            <FloatField
              field="investimentoInovacao"
              schema={schema}
              useFormRef={useFormRef}
            />
          </FormControl>
        </Grid>
        <Grid
          size={{
            md: 9
          }}>
          <Typography>
            Deste investimento total em P&D, quanto foi investido em P&D Tech?
            <HelpOutlineIcon onClick={handleClickPeD} sx={{ ml: 1 }} />
          </Typography>
          <Popover
            open={Boolean(anchorElPeD)}
            anchorEl={anchorElPeD}
            onClose={handleClosePeD}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <Typography sx={{ p: 2 }}>
              <b>P&D Tech</b>: Todo investimento em pesquisa que:
              <br />
              Contempla pesquisa básica, pesquisa básica dirigida, pesquisa
              aplicada, customizações, testes,
              <br /> prototipagem e desenvolvimento experimental que envolvam
              criação tecnológica, aprimoramento tecnológico <br /> de produtos
              ou processos, ou avanço do conhecimento, alinhado à estratégia e
              objetivos de negócios das empresas,
              <br /> e que se destinem a eventual incorporação nas atividades e
              negócios das empresas, caracterizados,
              <br /> ainda, pela incerteza quanto ao alcance do resultado.
              <br />
              A aquisição/aluguel de equipamentos/ativos comerciais, ainda que
              não testados internamente, só podem <br /> ser orçadas em P&D
              Tecnologia para testes de hipóteses, quando o risco/dúvida em
              relação ao sucesso for
              <br /> significativo (devendo a área cliente apresentar o
              embasamento técnico justificando a ação).
              <br />
              Essa aquisição deve-se limitar a uma quantidade mínima de ativos
              necessários para o teste de hipótese.
              <br />
              Priorizar testes com aluguel, leasing ou comodato em detrimento da
              compra de ativos,
              <br />
              minimizando o risco de apropriação contábil indevida.
              <br />
              As atividades podem ser exclusivamente internas (realizadas por
              profissionais e áreas da empresa)
              <br />
              e/ou externas (por profissionais e instituições terceirizadas)
              realizadas em parceria com
              <br />
              instituições públicas ou privadas, com a devida troca de
              conhecimentos e participação dos
              <br /> membros das instituições participantes, ou via aquisição de
              serviços e encomendas tecnológicas,
              <br />
              desde que estas incluam atividades para desenvolver e aprimorar o
              estado da arte.
              <br />
              Saiba mais clicando
              <a
                style={{ marginLeft: 2 }}
                href="https://venidera.sharepoint.com/:b:/s/IBRAM/EcwgpqbMq_FAkSnixCwSvKkBDkm6YCANstEi2UdTc4fN2w?e=yAqIZr"
                target="_blank" rel="noreferrer"
              >
                aqui
              </a>
              .
            </Typography>
          </Popover>
        </Grid>
        <Grid
          size={{
            md: 3
          }}>
          <FormControl fullWidth>
            <FloatField
              field="investimentoPdtech"
              schema={schema}
              useFormRef={useFormRef}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Divider>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <h3>Ferramentas</h3>
          <HelpOutlineIcon onClick={handleClickFerr} />
          <Popover
            open={Boolean(anchorElFerr)}
            anchorEl={anchorElFerr}
            onClose={handleCloseFerr}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <Typography sx={{ p: 2 }}>
              O objetivo desta seção é identificar as ferramentas que podem
              auxiliar
              <br />
              as empresas em seus processos/atividades de inovação
            </Typography>
          </Popover>
        </Stack>
      </Divider>
      <Ferramentas
        ano={ano}
        empresa={empresa}
        schema={schema}
        useFormRef={useFormRef}
      />
      <Divider sx={{mt:3}}>
        <Stack direction="row" spacing={2} sx={{alignItems:'center'}}>
          <h3>Programas Externos</h3>
          <HelpOutlineIcon onClick={handleClickPE} />
          <Popover
            open={Boolean(anchorElPE)}
            anchorEl={anchorElPE}
            onClose={handleClosePE}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <Typography sx={{ p: 2 }}>
              O objetivo desta seção é avaliar como a sua empresa fomenta
              programas externos
              <br />
              de inovação, tais como:
              <br />
              - programas para startups; <br />
              - programas em parceria com universidades;
              <br />
              - hackathons externos;
              <br />
              - participação em hubs (Mininghub, FiemgLab, Findes, etc)
              <br />
              <br />
              <b>Observações:</b>
              - Programas de fomento ao empreendedorismo, geração de spin-offs
              <br />
              transferência de tecnologia junto a universidades devem ser
              incluidos.
              <br />- Projetos específicos e convênios não precisam ser
              considerados.
            </Typography>
          </Popover>
        </Stack>
      </Divider>
      <FormControl fullWidth>
        <Checkbox
          sx={{ m: 1 }}
          field="pePocs"
          schema={schema}
          useFormRef={useFormRef}
        />
      </FormControl>
      <Grid container spacing={2} sx={{
        alignItems: 'center',
        '& .MuiTypography-root': { textAlign: 'right' },
        '& .MuiGrid-root': { p: 1 },
      }}>
        <Grid
          size={{
            md: 9
          }}>
          <Typography>Número de trabalhos desenvolvidos:</Typography>
        </Grid>
        <Grid
          size={{
            md: 3
          }}>
          <FormControl fullWidth>
            <IntegerField
              field="peNumPocs"
              schema={schema}
              useFormRef={useFormRef}
              size="small"
              disabled={Boolean(disabledPePocs())}
            />
          </FormControl>
        </Grid>
      </Grid>
      <FormControl fullWidth>
        <Checkbox
          sx={{ m: 1 }}
          field="peHubs"
          schema={schema}
          useFormRef={useFormRef}
        />
      </FormControl>
      <FormControl fullWidth>
        <TextField
          field="peListaHubs"
          multiline
          rows={4}
          schema={schema}
          useFormRef={useFormRef}
          disabled={Boolean(disabledPeHubs())}
          inputProps={{ maxLength: 1000 }}
        />
      </FormControl>
      <FormControl fullWidth>
        <Checkbox
          sx={{ m: 1 }}
          field="peHackathons"
          schema={schema}
          useFormRef={useFormRef}
        />
      </FormControl>
      <Grid container spacing={2} sx={{
        alignItems: 'center',
        '& .MuiTypography-root': { textAlign: 'right' },
        '& .MuiGrid-root': { p: 1 },
      }}>
        <Grid
          size={{
            md: 9
          }}>
          <Typography>Número de hackathon/bootcamps externos:</Typography>
        </Grid>
        <Grid
          size={{
            md: 3
          }}>
          <FormControl fullWidth>
            <IntegerField
              field="peNumHackathons"
              schema={schema}
              useFormRef={useFormRef}
              size="small"
              disabled={Boolean(disabledpeHackathons())}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Divider sx={{mt:3}}>
        <Stack direction="row" spacing={2} sx={{alignItems: 'center'}}>
          <h3>Ecossistema</h3>
          <HelpOutlineIcon onClick={handleClickEco} />
          <Popover
            open={Boolean(anchorElEco)}
            anchorEl={anchorElEco}
            onClose={handleCloseEco}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            <Typography sx={{ p: 2 }}>
              O objetivo desta seção é identificar as “Mining Techs” atualmente
              em operação no mercado.
            </Typography>
          </Popover>
        </Stack>
      </Divider>
      <Ecossistema
        ano={ano}
        empresa={empresa}
        schema={schema}
        useFormRef={useFormRef}
      />
      <Eventos ano={ano} empresa={empresa} />
      <Premiacoes ano={ano} empresa={empresa} />
      <Divider sx={{ mt: 3 }}>
        <h3>Observações Finais</h3>
      </Divider>
      <DialogContentText sx={{ mb: 2 }}>
        Deixe seu comentário ou outras informações que considerar importante
        inclusive as ligadas a dúvidas, sugestões em geral ou de novos temas a
        serem abordados, críticas e elogios:
      </DialogContentText>
      <FormControl fullWidth>
        <TextField
          sx={{ mb: 2 }}
          field="observacoes"
          multiline
          rows={4}
          schema={schema}
          useFormRef={useFormRef}
          inputProps={{ maxLength: 1000 }}
        />
      </FormControl>
      <Box>
        <Button
          variant="outlined"
          sx={{ mr: 2 }}
          onClick={() => {
            reset();
          }}
          disabled={!isDirty || isSubmitting}
        >
          CANCELAR
        </Button>
        <Button
          type="submit"
          variant="contained"
          sx={{ m: 1 }}
          onClick={handleSubmit(submit)}
          disabled={!isDirty || isSubmitting}
        >
          SALVAR
        </Button>
      </Box>
    </div>)
  );
};

export default DadosGerais;
